import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  // Project
  SearchProjectClient,
  GetProjectClient,
  DeleteProjectClient,
  UpdateProjectClient,
  CreateProjectClient,
  ArchivingProjectClient,
  // RealEstate
  SearchRealEstateClient,
  GetRealEstateClient,
  DeleteRealEstateClient,
  UpdateRealEstateClient,
  CreateRealEstateClient,
  ArchivingRealEstateClient,
  // Contracts
  SearchContractClient,
  GetContractClient,
  DeleteContractClient,
  UpdateContractClient,
  CreateContractClient,
  // ContractAddendum
  GetContractAddendumsByContractIdClient,
  CreateContractAddendumClient,
  UpdateContractAddendumClient,
  DeleteContractAddendumClient,
  // ConstructionSite
  SearchConstructionSiteClient,
  CreateConstructionSiteClient,
  UpdateConstructionSiteClient,
  DeleteConstructionSiteClient,
  GetConstructionSiteClient,
  // Block
  CreateBlockClient,
  UpdateBlockClient,
  DeleteBlockClient,
  GetBlockClient,
  SearchBlockClient,
  // BuildingBlockType
  CreateBuildingBlockTypeClient,
  UpdateBuildingBlockTypeClient,
  DeleteBuildingBlockTypeClient,
  GetBuildingBlockTypeClient,
  SearchBuildingBlockTypeClient,
  // Plot
  CreatePlotClient,
  UpdatePlotClient,
  DeletePlotClient,
  GetPlotClient,
  SearchPlotClient,
  // TimeKeepingTemplate
  CreateTimeKeepingTemplateClient,
  UpdateTimeKeepingTemplateClient,
  DeleteTimeKeepingTemplateClient,
  GetTimeKeepingTemplateClient,
  SearchTimeKeepingTemplateClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

// API Clients
// Project
const searchProjectClient = new SearchProjectClient(SERVICE_URL)
const getProjectClient = new GetProjectClient(SERVICE_URL)
const createProjectClient = new CreateProjectClient(SERVICE_URL)
const deleteProjectClient = new DeleteProjectClient(SERVICE_URL)
const updateProjectClient = new UpdateProjectClient(SERVICE_URL)
const archivingProjectClient = new ArchivingProjectClient(SERVICE_URL)

// RealEstate
const searchRealEstateClient = new SearchRealEstateClient(SERVICE_URL)
const getRealEstateClient = new GetRealEstateClient(SERVICE_URL)
const createRealEstateClient = new CreateRealEstateClient(SERVICE_URL)
const deleteRealEstateClient = new DeleteRealEstateClient(SERVICE_URL)
const updateRealEstateClient = new UpdateRealEstateClient(SERVICE_URL)
const archivingRealEstateClient = new ArchivingRealEstateClient(SERVICE_URL)

// Contracts
const searchContractClient = new SearchContractClient(SERVICE_URL)
const getContractClient = new GetContractClient(SERVICE_URL)
const createContractClient = new CreateContractClient(SERVICE_URL)
const deleteContractClient = new DeleteContractClient(SERVICE_URL)
const updateContractClient = new UpdateContractClient(SERVICE_URL)

// ContractAddendum
const getContractAddendumsByContractIdClient = new GetContractAddendumsByContractIdClient(SERVICE_URL)
const createContractAddendumClient = new CreateContractAddendumClient(SERVICE_URL)
const updateContractAddendumClient = new UpdateContractAddendumClient(SERVICE_URL)
const deleteContractAddendumClient = new DeleteContractAddendumClient(SERVICE_URL)

// ConstructionSite
const searchConstructionSiteClient = new SearchConstructionSiteClient(SERVICE_URL)
const createConstructionSiteClient = new CreateConstructionSiteClient(SERVICE_URL)
const updateConstructionSiteClient = new UpdateConstructionSiteClient(SERVICE_URL)
const deleteConstructionSiteClient = new DeleteConstructionSiteClient(SERVICE_URL)
const getConstructionSiteClient = new GetConstructionSiteClient(SERVICE_URL)

// Block
const createBlockClient = new CreateBlockClient(SERVICE_URL)
const updateBlockClient = new UpdateBlockClient(SERVICE_URL)
const deleteBlockClient = new DeleteBlockClient(SERVICE_URL)
const getBlockClient = new GetBlockClient(SERVICE_URL)
const searchBlockClient = new SearchBlockClient(SERVICE_URL)

// BuildingBlockType
const createBuildingBlockTypeClient = new CreateBuildingBlockTypeClient(SERVICE_URL)
const updateBuildingBlockTypeClient = new UpdateBuildingBlockTypeClient(SERVICE_URL)
const deleteBuildingBlockTypeClient = new DeleteBuildingBlockTypeClient(SERVICE_URL)
const getBuildingBlockTypeClient = new GetBuildingBlockTypeClient(SERVICE_URL)
const searchBuildingBlockTypeClient = new SearchBuildingBlockTypeClient(SERVICE_URL)

// Plot
const createPlotClient = new CreatePlotClient(SERVICE_URL)
const updatePlotClient = new UpdatePlotClient(SERVICE_URL)
const deletePlotClient = new DeletePlotClient(SERVICE_URL)
const getPlotClient = new GetPlotClient(SERVICE_URL)
const searchPlotClient = new SearchPlotClient(SERVICE_URL)

// TimeKeepingTemplate
const createTimeKeepingTemplateClient = new CreateTimeKeepingTemplateClient(SERVICE_URL)
const updateTimeKeepingTemplateClient = new UpdateTimeKeepingTemplateClient(SERVICE_URL)
const deleteTimeKeepingTemplateClient = new DeleteTimeKeepingTemplateClient(SERVICE_URL)
const getTimeKeepingTemplateClient = new GetTimeKeepingTemplateClient(SERVICE_URL)
const searchTimeKeepingTemplateClient = new SearchTimeKeepingTemplateClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  isArchived: false,
  excel: null,
  project: null,
  detail: null,
  realEstate: null,
  contracts: null,
  contractAddendums: null,
  constructionsite: null,
  block: null,
  buildingblocktype: null,
  plot: null,
  timekeepingtemplate: null,
}

// Async Thunks
// Project
export const projectGetAll = createAsyncThunk('project/getAll', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await searchProjectClient.search(
      params.name,
      params.projectStateId,
      params.items_per_page,
      params.page,
      params.order,
      params.sort
    )
    dispatch(setProject(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const projectExcel = createAsyncThunk(
  'project/excel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchProjectClient.excel(
        params.name,
        params.projectStateId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const projectGetId = createAsyncThunk('project/getId', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await getProjectClient.get(id)
    dispatch(setDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const projectDelete = createAsyncThunk('project/delete', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await deleteProjectClient.delete(params)
    dispatch(setDeleted(true))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const projectArchiving = createAsyncThunk(
  'project/archiving',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await archivingProjectClient.archive(params)
      dispatch(setArchived(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const projectCreate = createAsyncThunk('project/create', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await createProjectClient.create(params)
    dispatch(setRedirect(response || params || true))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const projectUpdate = createAsyncThunk('project/update', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await updateProjectClient.update(params)
    dispatch(setRedirect(response || params || true))
    dispatch(setDetail(params))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setDetail(params))
    dispatch(setError(error))
    throw error
  }
})

// RealEstate
export const realEstateGetAll = createAsyncThunk(
  'project/realEstateGetAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchRealEstateClient.search(
        params.name,
        params.estateTypeId,
        params.estateStateId,
        params.projectId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setRealEstate(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const realEstateExcel = createAsyncThunk(
  'project/realEstateExcel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchRealEstateClient.excel(
        params.name,
        params.estateTypeId,
        params.estateStateId,
        params.projectId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const realEstateGetId = createAsyncThunk(
  'project/realEstateGetId',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getRealEstateClient.get(id)
      dispatch(setDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const realEstateDelete = createAsyncThunk(
  'project/realEstateDelete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await deleteRealEstateClient.delete(params)
      dispatch(setDeleted(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const realEstateArchiving = createAsyncThunk(
  'project/realEstateArchiving',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await archivingRealEstateClient.archive(params)
      dispatch(setArchived(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const realEstateCreate = createAsyncThunk(
  'project/realEstateCreate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createRealEstateClient.create(params)
      dispatch(setRedirect(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const realEstateUpdate = createAsyncThunk(
  'project/realEstateUpdate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateRealEstateClient.update(params)
      dispatch(setRedirect(response || params || true))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

// Contracts
export const contractsGetAll = createAsyncThunk(
  'project/contractsGetAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchContractClient.search(
        params.name,
        params.projectId,
        params.startDate,
        params.endDate,
        params.contractTypeId,
        params.contractStateId,
        params.companyId,
        params.contractName,
        params.isExpired,
        params.expiringInDays,
        params.currentEndDateStart,
        params.currentEndDateEnd,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setContracts(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const contractsExcel = createAsyncThunk(
  'project/contractsExcel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))

      const response = await searchContractClient.excel(
        params.name,
        params.projectId,
        params.startDate,
        params.endDate,
        params.contractTypeId,
        params.contractStateId,
        params.companyId,
        params.contractName,
        params.isExpired,
        params.expiringInDays,
        params.currentEndDateStart,
        params.currentEndDateEnd,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const contractsGetId = createAsyncThunk('project/contractsGetId', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await getContractClient.get(id)
    dispatch(setDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const contractsDelete = createAsyncThunk(
  'project/contractsDelete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await deleteContractClient.delete(params)
      dispatch(setDeleted(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const contractsCreate = createAsyncThunk(
  'project/contractsCreate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createContractClient.create(params)
      dispatch(setRedirect(response || params || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const contractsUpdate = createAsyncThunk(
  'project/contractsUpdate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateContractClient.update(params)
      dispatch(setRedirect(response || params || true))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

// ContractAddendum
export const contractAddendumGetAll = createAsyncThunk(
  'project/contractAddendumGetAll',
  async (contractId, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getContractAddendumsByContractIdClient.list(contractId)
      dispatch(setContractAddendums(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const contractAddendumCreate = createAsyncThunk(
  'project/contractAddendumCreate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createContractAddendumClient.create(params)
      dispatch(setRedirect(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const contractAddendumUpdate = createAsyncThunk(
  'project/contractAddendumUpdate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateContractAddendumClient.update(params)
      dispatch(setRedirect(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const contractAddendumDelete = createAsyncThunk(
  'project/contractAddendumDelete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      const response = await deleteContractAddendumClient.delete(params)
      dispatch(setLoading(false))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// ConstructionSite
export const constructionsiteGetAll = createAsyncThunk(
  'project/constructionsiteGetAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchConstructionSiteClient.search(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setConstructionSite(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const constructionsiteExcel = createAsyncThunk(
  'project/constructionsiteExcel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchConstructionSiteClient.excel(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const constructionsiteGetId = createAsyncThunk(
  'project/constructionsiteGetId',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getConstructionSiteClient.get(id)
      dispatch(setDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const constructionsiteDelete = createAsyncThunk(
  'project/constructionsiteDelete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await deleteConstructionSiteClient.delete(params)
      dispatch(setDeleted(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const constructionsiteCreate = createAsyncThunk(
  'project/constructionsiteCreate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createConstructionSiteClient.create(params)
      dispatch(setRedirect(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const constructionsiteUpdate = createAsyncThunk(
  'project/constructionsiteUpdate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateConstructionSiteClient.update(params)
      dispatch(setRedirect(response || params || true))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

// Block
export const blockGetAll = createAsyncThunk('project/blockGetAll', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await searchBlockClient.search(
      params.name,
      params.items_per_page,
      params.page,
      params.order,
      params.sort
    )
    dispatch(setBlock(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const blockExcel = createAsyncThunk(
  'project/blockExcel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchBlockClient.excel(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const blockGetId = createAsyncThunk('project/blockGetId', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await getBlockClient.get(id)
    dispatch(setDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const blockDelete = createAsyncThunk('project/blockDelete', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await deleteBlockClient.delete(params)
    dispatch(setDeleted(true))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const blockCreate = createAsyncThunk('project/blockCreate', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await createBlockClient.create(params)
    dispatch(setRedirect(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const blockUpdate = createAsyncThunk('project/blockUpdate', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await updateBlockClient.update(params)
    dispatch(setRedirect(response || params || true))
    dispatch(setDetail(params))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setDetail(params))
    dispatch(setError(error))
    throw error
  }
})

// BuildingBlockType
export const buildingblocktypeGetAll = createAsyncThunk(
  'project/buildingblocktypeGetAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchBuildingBlockTypeClient.search(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setBuildingBlockType(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const buildingblocktypeExcel = createAsyncThunk(
  'project/buildingblocktypeExcel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchBuildingBlockTypeClient.excel(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const buildingblocktypeGetId = createAsyncThunk(
  'project/buildingblocktypeGetId',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getBuildingBlockTypeClient.get(id)
      dispatch(setDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const buildingblocktypeDelete = createAsyncThunk(
  'project/buildingblocktypeDelete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await deleteBuildingBlockTypeClient.delete(params)
      dispatch(setDeleted(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const buildingblocktypeCreate = createAsyncThunk(
  'project/buildingblocktypeCreate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createBuildingBlockTypeClient.create(params)
      dispatch(setRedirect(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const buildingblocktypeUpdate = createAsyncThunk(
  'project/buildingblocktypeUpdate',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateBuildingBlockTypeClient.update(params)
      dispatch(setRedirect(response || params || true))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

// Plot
export const plotGetAll = createAsyncThunk('project/plotGetAll', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await searchPlotClient.search(
      params.name,
      params.items_per_page,
      params.page,
      params.order,
      params.sort
    )
    dispatch(setPlot(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const plotExcel = createAsyncThunk(
  'project/plotExcel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchPlotClient.excel(
        params.name,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const plotGetId = createAsyncThunk('project/plotGetId', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await getPlotClient.get(id)
    dispatch(setDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const plotDelete = createAsyncThunk('project/plotDelete', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await deletePlotClient.delete(params)
    dispatch(setDeleted(true))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const plotCreate = createAsyncThunk('project/plotCreate', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await createPlotClient.create(params)
    dispatch(setRedirect(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const plotUpdate = createAsyncThunk('project/plotUpdate', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await updatePlotClient.update(params)
    dispatch(setRedirect(response || params || true))
    dispatch(setDetail(params))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setDetail(params))
    dispatch(setError(error))
    throw error
  }
})

// TimeKeepingTemplate
export const timeKeepingTemplateGetAll = createAsyncThunk('project/timeKeepingTemplateGetAll', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    dispatch(setDeleted(false))
    const response = await searchTimeKeepingTemplateClient.search(
      params.name,
      params.projectId,
      params.timeKeepingTypeId,
      params.items_per_page,
      params.page,
      params.order,
      params.sort
    )
    dispatch(setTimeKeepingTemplate(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const timeKeepingTemplateExcel = createAsyncThunk(
  'project/timeKeepingTemplateExcel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchTimeKeepingTemplateClient.excel(
        params.name,
        params.projectId,
        params.timeKeepingTypeId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const timeKeepingTemplateGetId = createAsyncThunk('project/timeKeepingTemplateGetId', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await getTimeKeepingTemplateClient.get(id)
    dispatch(setDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const timeKeepingTemplateDelete = createAsyncThunk('project/timeKeepingTemplateDelete', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await deleteTimeKeepingTemplateClient.delete(params)
    dispatch(setDeleted(true))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const timeKeepingTemplateCreate = createAsyncThunk('project/timeKeepingTemplateCreate', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await createTimeKeepingTemplateClient.create(params)
    dispatch(setRedirect(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const timeKeepingTemplateUpdate = createAsyncThunk('project/timeKeepingTemplateUpdate', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await updateTimeKeepingTemplateClient.update(params)
    dispatch(setRedirect(response || params || true))
    dispatch(setDetail(params))
    return response
  } catch (error) {
    dispatch(setDetail(params))
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

// Slice
const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setProject: (state, action) => {
      state.project = action.payload
      state.loading = false
    },
    setDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setDeleted: (state, action) => {
      state.isDeleted = action.payload
      state.loading = false
    },
    setRedirectPage: (state, action) => {
      state.isRedirectPage = action.payload
      state.loading = false
    },
    setRedirect: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setArchived: (state, action) => {
      state.isArchived = action.payload
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    setRealEstate: (state, action) => {
      state.realEstate = action.payload
      state.loading = false
    },
    setContracts: (state, action) => {
      state.contracts = action.payload
      state.loading = false
    },
    setContractAddendums: (state, action) => {
      state.contractAddendums = action.payload
      state.loading = false
    },
    setConstructionSite: (state, action) => {
      state.constructionsite = action.payload
      state.loading = false
    },
    setBlock: (state, action) => {
      state.block = action.payload
      state.loading = false
    },
    setBuildingBlockType: (state, action) => {
      state.buildingblocktype = action.payload
      state.loading = false
    },
    setPlot: (state, action) => {
      state.plot = action.payload
      state.loading = false
    },
    setTimeKeepingTemplate: (state, action) => {
      state.timekeepingtemplate = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('project/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

// Export actions
export const {
  setLoading,
  setError,
  setProject,
  setDetail,
  setDeleted,
  setRedirectPage,
  setRedirect,
  setArchived,
  setExcel,
  setRealEstate,
  setContracts,
  setContractAddendums,
  setConstructionSite,
  setBlock,
  setBuildingBlockType,
  setPlot,
  setTimeKeepingTemplate,
  resetState,
} = projectSlice.actions

// Selectors
export const selectProjectLoading = (state) => state.project.loading
export const selectProjectError = (state) => state.project.error
export const selectProject = (state) => state.project.project
export const selectProjectDetail = (state) => state.project.detail
export const selectProjectDeleted = (state) => state.project.isDeleted
export const selectProjectRedirectPage = (state) => state.project.isRedirectPage
export const selectProjectRedirect = (state) => state.project.isRedirect
export const selectProjectArchived = (state) => state.project.isArchived
export const selectProjectExcel = (state) => state.project.excel
export const selectProjectRealEstate = (state) => state.project.realEstate
export const selectProjectContracts = (state) => state.project.contracts
export const selectProjectContractAddendums = (state) => state.project.contractAddendums
export const selectProjectConstructionSite = (state) => state.project.constructionsite
export const selectProjectBlock = (state) => state.project.block
export const selectProjectBuildingBlockType = (state) => state.project.buildingblocktype
export const selectProjectPlot = (state) => state.project.plot
export const selectProjectTimeKeepingTemplate = (state) => state.project.timekeepingtemplate

export default projectSlice.reducer
